// /opt/isp_erp/frontend/src/pages/warehouse/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  Devices as DevicesIcon,
  PendingActions as PendingIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import TipoFallaSelect from '../../components/warehouse/TipoFallaSelect';
import ResumenDiario from '../../components/warehouse/ResumenDiario';

const WarehouseDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [searchSn, setSearchSn] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recepcionOpen, setRecepcionOpen] = useState(false);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [recepcionData, setRecepcionData] = useState({
    observaciones: '',
    necesita_rma: false,
    diagnostico_rma: '',
    tipo_falla: ''
  });

  useEffect(() => {
    cargarEstadisticas();
    cargarPendientes();
  }, []);

  const cargarEstadisticas = async () => {
    try {
      const response = await warehouseAPI.getEstadisticas();
      setStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const cargarPendientes = async () => {
    try {
      const response = await warehouseAPI.getPendientesRetiro();
      setPendientes(response.data);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
    }
  };

  const buscarEquipo = async () => {
    if (!searchSn) return;
    setLoading(true);
    try {
      const response = await warehouseAPI.buscarEquipo(searchSn);
      setSearchResult(response.data);
      
      if (response.data.estado === 'RETIRO_PENDIENTE') {
        setSelectedEquipo(response.data);
        setRecepcionOpen(true);
      }
    } catch (error) {
      console.error('Error buscando equipo:', error);
      setSearchResult(null);
    } finally {
      setLoading(false);
    }
  };

  const registrarRecepcion = async () => {
    try {
      await warehouseAPI.recepcionEquipo({
        sn: selectedEquipo.sn,
        observaciones: recepcionData.observaciones,
        necesita_rma: recepcionData.necesita_rma,
        diagnostico_rma: recepcionData.diagnostico_rma,
        tipo_falla: recepcionData.necesita_rma ? (recepcionData.tipo_falla || null) : null
      });
      
      setRecepcionOpen(false);
      setSearchResult(null);
      setRecepcionData({
        observaciones: '',
        necesita_rma: false,
        diagnostico_rma: '',
        tipo_falla: ''
      });
      cargarEstadisticas();
      cargarPendientes();
    } catch (error) {
      console.error('Error registrando recepción:', error);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value || 0}
            </Typography>
          </Box>
          <Box sx={{ color: color || '#3b82f6' }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getEstadoColor = (estado) => {
    const colors = {
      BODEGA: 'success',
      EN_TRANSITO: 'warning',
      INSTALADO: 'info',
      RETIRO_PENDIENTE: 'error',
      RMA_BAJA: 'default'
    };
    return colors[estado] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Bodega
      </Typography>

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="Total Equipos" value={stats.total_equipos} icon={<WarehouseIcon sx={{ fontSize: 40 }} />} />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="En Bodega" value={stats.en_bodega} icon={<InventoryIcon sx={{ fontSize: 40 }} />} color="#10b981" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="En Tránsito" value={stats.en_transito} icon={<DevicesIcon sx={{ fontSize: 40 }} />} color="#f59e0b" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="Instalados" value={stats.instalados} icon={<CheckCircleIcon sx={{ fontSize: 40 }} />} color="#3b82f6" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="Retiro Pendiente" value={stats.retiro_pendiente} icon={<PendingIcon sx={{ fontSize: 40 }} />} color="#ef4444" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard title="RMA/Baja" value={stats.rma_baja} icon={<BuildIcon sx={{ fontSize: 40 }} />} color="#6b7280" />
          </Grid>
        </Grid>
      )}

      {/* Búsqueda Rápida */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Búsqueda Rápida por SN
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ingrese número de serie del equipo"
              value={searchSn}
              onChange={(e) => setSearchSn(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarEquipo()}
            />
            <Button
              variant="contained"
              onClick={buscarEquipo}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Buscar
            </Button>
          </Box>

          {searchResult && (
            <Alert severity={searchResult.estado === 'RETIRO_PENDIENTE' ? 'warning' : 'info'} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Equipo {searchResult.sn} - {searchResult.estado}
              </Typography>
              <Typography variant="body2">Modelo: {searchResult.modelo}</Typography>
              <Typography variant="body2">
                Poseedor: {searchResult.poseedor?.nombre} ({searchResult.poseedor?.tipo})
              </Typography>
              {searchResult.ultima_accion && (
                <Typography variant="body2">
                  Última acción: {new Date(searchResult.ultima_accion.fecha).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Equipos Pendientes */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Equipos Pendientes de Devolución
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SN</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Técnico</TableCell>
                  <TableCell>Fecha Retiro</TableCell>
                  <TableCell>Días Pendiente</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendientes.map((item) => (
                  <TableRow key={item.equipo?.id}>
                    <TableCell>{item.equipo?.sn}</TableCell>
                    <TableCell>{item.equipo?.modelo}</TableCell>
                    <TableCell>{item.tecnico?.nombre}</TableCell>
                    <TableCell>
                      {item.fecha_retiro ? new Date(item.fecha_retiro).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${item.dias_pendiente} días`}
                        color={item.dias_pendiente > 2 ? 'error' : item.dias_pendiente > 1 ? 'warning' : 'info'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedEquipo(item.equipo);
                          setRecepcionOpen(true);
                        }}
                      >
                        Recibir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ResumenDiario />

      {/* Dialog de Recepción */}
      <Dialog open={recepcionOpen} onClose={() => setRecepcionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recepción de Equipo en Bodega</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observaciones"
              value={recepcionData.observaciones}
              onChange={(e) => setRecepcionData({ ...recepcionData, observaciones: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={recepcionData.necesita_rma}
                  onChange={(e) => setRecepcionData({ ...recepcionData, necesita_rma: e.target.checked })}
                />
              }
              label="¿Necesita RMA?"
            />
            {recepcionData.necesita_rma && (
              <TipoFallaSelect
                value={recepcionData.tipo_falla}
                onChange={(valor) => setRecepcionData({ ...recepcionData, tipo_falla: valor })}
                required
              />
            )}

            {recepcionData.necesita_rma && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Diagnóstico RMA"
                value={recepcionData.diagnostico_rma}
                onChange={(e) => setRecepcionData({ ...recepcionData, diagnostico_rma: e.target.value })}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecepcionOpen(false)}>Cancelar</Button>
          <Button onClick={registrarRecepcion} variant="contained" color="primary" disabled={recepcionData.necesita_rma && !recepcionData.tipo_falla}>
            Registrar Recepción
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehouseDashboard;
